import React from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { Card, CardHeader, CardTitle, CardBody, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalBody, Button, Form, FormGroup, Label, Input } from "reactstrap";
import { authenticatedFetch } from "../services/authService";
import api from "../API/api";

const localizer = momentLocalizer(moment);

const CALENDARS = [
  { key: "practice",   id: api.calendarPractice, label: "Practice",    color: "#1d8cf8" },
  { key: "tourney",    id: api.calendarTourney,  label: "Tournament",  color: "#1d8cf8" },
  { key: "dropin",     id: api.calendarDropin,   label: "Drop-in",     color: "#1d8cf8" },
];

function parseGoogleEvents(items, color) {
  return (items || []).map((item) => {
    const isAllDay = !!(item.start.date);
    let start, end;
    if (isAllDay) {
      start = moment(item.start.date).startOf("day").toDate();
      // Google all-day end is exclusive — subtract 1 day
      end = moment(item.end.date).subtract(1, "day").endOf("day").toDate();
    } else {
      start = new Date(item.start.dateTime);
      end = new Date(item.end.dateTime);
    }
    return {
      title: item.summary || "(No title)",
      start,
      end,
      allDay: isAllDay,
      resource: { color, location: item.location, description: item.description },
    };
  });
}

const Calendar = () => {
  const [activeTab, setActiveTab] = React.useState("practice");
  const [eventsMap, setEventsMap] = React.useState({ practice: [], tourney: [], dropin: [] });
  const [loading, setLoading] = React.useState({ practice: false, tourney: false, dropin: false });
  const [modal, setModal] = React.useState(false);
  const [slotInfo, setSlotInfo] = React.useState(null);
  const [form, setForm] = React.useState({ summary: "", location: "", description: "", start: "", end: "", allDay: false });
  const [saving, setSaving] = React.useState(false);

  const fetchCalendar = React.useCallback(async (calKey, calId) => {
    setLoading((prev) => ({ ...prev, [calKey]: true }));
    try {
      const res = await authenticatedFetch(api.calendar, {
        headers: { calendar: calId },
      });
      if (res.ok) {
        const data = await res.json();
        const cal = CALENDARS.find((c) => c.key === calKey);
        setEventsMap((prev) => ({ ...prev, [calKey]: parseGoogleEvents(data.items, cal.color) }));
      } else {
        console.error(`Calendar fetch failed for ${calKey}:`, res.status);
      }
    } catch (err) {
      console.error(`Calendar fetch error for ${calKey}:`, err);
    } finally {
      setLoading((prev) => ({ ...prev, [calKey]: false }));
    }
  }, []);

  React.useEffect(() => {
    CALENDARS.forEach((cal) => fetchCalendar(cal.key, cal.id));
  }, [fetchCalendar]);

  const activeCal = CALENDARS.find((c) => c.key === activeTab);

  const handleSelectSlot = (slot) => {
    setSlotInfo(slot);
    const startDt = moment(slot.start);
    // Subtract 1 minute to fix exclusive end from drag selection
    const endDt = moment(slot.end).subtract(1, "minute");
    setForm({
      summary: "",
      location: "",
      description: "",
      start: startDt.format("YYYY-MM-DDTHH:mm"),
      end: endDt.format("YYYY-MM-DDTHH:mm"),
      allDay: false,
    });
    setModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.summary.trim()) return;
    setSaving(true);
    try {
      let startDt, endDt;
      if (form.allDay) {
        startDt = moment(form.start.split("T")[0]).startOf("day").toISOString();
        endDt = moment(form.end.split("T")[0]).endOf("day").toISOString();
      } else {
        startDt = new Date(form.start).toISOString();
        endDt = new Date(form.end).toISOString();
      }
      const res = await authenticatedFetch(api.calendar, {
        method: "POST",
        headers: { calendar: activeCal.id },
        body: JSON.stringify({
          summary: form.summary,
          location: form.location,
          description: form.description,
          start: startDt,
          end: endDt,
        }),
      });
      if (res.ok) {
        setModal(false);
        fetchCalendar(activeTab, activeCal.id);
      } else {
        const err = await res.json();
        console.error("Failed to create event:", err);
        alert("Failed to create event: " + (err.message || res.status));
      }
    } catch (err) {
      console.error("Error creating event:", err);
      alert("Error creating event: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.resource?.color || activeCal.color,
      borderColor: event.resource?.color || activeCal.color,
      color: "#fff",
      borderRadius: "4px",
    },
  });

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card className="card-calendar">
              <CardHeader>
                <CardTitle tag="h4">Calendars</CardTitle>
                <Nav className="nav-pills-info" pills>
                  {CALENDARS.map((cal) => (
                    <NavItem key={cal.key}>
                      <NavLink
                        data-toggle="tab"
                        href="#pablo"
                        className={activeTab === cal.key ? "active" : ""}
                        onClick={(e) => { e.preventDefault(); setActiveTab(cal.key); }}
                      >
                        {loading[cal.key] ? `${cal.label} …` : cal.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </CardHeader>
              <CardBody>
                <TabContent className="tab-space" activeTab={activeTab}>
                  {CALENDARS.map((cal) => (
                    <TabPane key={cal.key} tabId={cal.key}>
                      <BigCalendar
                        selectable
                        localizer={localizer}
                        events={eventsMap[cal.key]}
                        defaultView="month"
                        views={["month", "week", "day"]}
                        scrollToTime={new Date(1970, 1, 1, 6)}
                        defaultDate={new Date()}
                        onSelectSlot={handleSelectSlot}
                        eventPropGetter={eventStyleGetter}
                        style={{ minHeight: 600 }}
                      />
                    </TabPane>
                  ))}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" modalClassName="modal-black">
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setModal(false)}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">Add Event — {activeCal.label}</h6>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Title *</Label>
              <Input name="summary" value={form.summary} onChange={handleFormChange} placeholder="Event title" />
            </FormGroup>
            <FormGroup check className="mb-3">
              <Label check>
                <Input type="checkbox" name="allDay" checked={form.allDay} onChange={handleFormChange} />{" "}
                All-day / Multi-day event
              </Label>
            </FormGroup>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Start</Label>
                  <Input
                    type={form.allDay ? "date" : "datetime-local"}
                    name="start"
                    value={form.allDay ? form.start.split("T")[0] : form.start}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>End</Label>
                  <Input
                    type={form.allDay ? "date" : "datetime-local"}
                    name="end"
                    value={form.allDay ? form.end.split("T")[0] : form.end}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Location</Label>
              <Input name="location" value={form.location} onChange={handleFormChange} placeholder="Optional" />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input type="textarea" name="description" value={form.description} onChange={handleFormChange} placeholder="Optional" rows={3} />
            </FormGroup>
          </Form>
        </ModalBody>
        <div className="modal-footer">
          <Button color="default" onClick={() => setModal(false)} disabled={saving}>Cancel</Button>
          <Button color="info" onClick={handleSave} disabled={saving || !form.summary.trim()}>
            {saving ? "Saving…" : "Add Event"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Calendar;
